import { useState, useEffect } from 'react';
import { useSettingsStore } from '@/features/settings/stores/settings.store';
import { toast } from 'react-hot-toast';
import { AlertCircle } from 'lucide-react';
import { logger } from '@/features/help/utils/logger';

/**
 * Notifications section component for the Settings page
 */
export const NotificationsSection = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const {
    notificationEmail,
    notifyCrmOwner,
    isLoading,
    isSaving,
    error,
    fetchSettings,
    updateSettings,
  } = useSettingsStore();

  // Form state
  const [email, setEmail] = useState<string>('');
  const [notifyOwner, setNotifyOwner] = useState<boolean>(false);

  // Fetch settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      logger.info('SettingsFlow', 'Initializing notification settings component');
      await fetchSettings();
      setIsInitialized(true);
    };

    loadSettings();
  }, []);

  // Update local state when store values change
  useEffect(() => {
    if (isInitialized) {
      setEmail(notificationEmail || '');
      setNotifyOwner(notifyCrmOwner);
    }
  }, [notificationEmail, notifyCrmOwner, isInitialized]);

  // Handle email input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
  };

  // Handle checkbox change
  const handleNotifyOwnerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotifyOwner(e.target.checked);
  };

  // Save settings
  const handleSave = async () => {
    logger.info('SettingsFlow', 'Saving notification settings', {
      email: email || null,
      notifyOwner,
    });

    // Basic email validation
    if (email && !isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    const success = await updateSettings({
      notificationEmail: email || null,
      notifyCrmOwner: notifyOwner,
    });

    if (success) {
      logger.success('SettingsFlow', 'Notification settings saved successfully');
    }
  };

  // Simple email validation
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Determine if save button should be disabled
  const isSaveDisabled =
    isSaving ||
    isLoading ||
    (notificationEmail === (email || null) && notifyCrmOwner === notifyOwner);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Notification Settings</h2>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 p-3 rounded-lg">
          <div className="flex gap-2 items-center">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Default Notification Email */}
        <div>
          <label
            htmlFor="notification-email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Default email to receive notifications upon client application signs
          </label>
          <input
            id="notification-email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            disabled={isLoading}
            placeholder="Email address for notifications"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-gray-500">
            This email will receive notifications when clients complete and sign applications.
          </p>
        </div>

        {/* CRM Record Owner Notification */}
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="notify-crm-owner"
              type="checkbox"
              checked={notifyOwner}
              onChange={handleNotifyOwnerChange}
              disabled={isLoading}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="notify-crm-owner" className="font-medium text-gray-700">
              Notify the assigned CRM record owner
            </label>
            <p className="text-gray-500">
              When enabled, the user assigned to the record in the CRM will also receive
              notifications.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex">
          <button
            onClick={handleSave}
            disabled={isSaveDisabled}
            className={`px-4 py-2 rounded ${
              isSaveDisabled
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            } transition-colors`}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};
